import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Save, Sparkles, Tag, Flame, Gift, Package, 
  HelpCircle, Plus, Trash2, Eye, Star, Video, Youtube, Image as ImageIcon
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import ProductImageUploader from '../../components/ui/ProductImageUploader';
import { toast } from 'sonner';
import DatePicker from '../../components/ui/DatePicker';
import RichTextEditor from '../../components/ui/RichTextEditor';
import { fetchPujaProducts, formatPujaPackages, getPujaDescription, getPujaStartingPrice, isAvailableOffering } from '../../lib/pujaForm';
import './puja-form.css';

import API from '../../config/api';

const slugify = (text) => {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-');
};

const DAYS_OF_WEEK = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
];

const AdminPujaAdd = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [temples, setTemples] = useState([]);
  const [deities, setDeities] = useState([]);
  const [templeTypes, setTempleTypes] = useState([]);
  const [allBhets, setAllBhets] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  
  const [activeTab, setActiveTab] = useState('en'); // 'en' | 'hi'
  const [isCustomSlug, setIsCustomSlug] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    temple_id: '',
    deity: '',
    temple_type: '',
    duration: '1-2 hours',
    puja_date: '',
    is_offering: false,
    is_weekly: false,
    weekly_day: 'Monday',
    is_active: true,

    // Media
    image_url: '',
    gallery: [],
    video_url: '',
    youtube_url: '',

    // Rich Content & Descriptions (Bilingual)
    about_content_en: '',
    about_content_hi: '',
    puja_performed_by_en: 'Qualified Himalayan Vedic Pandits initiated into authentic tantric & smarta traditions.',
    puja_performed_by_hi: 'हिमालयी पीठ के सिद्ध कर्मकांडी वैदिक ब्राह्मणों द्वारा संपूर्ण विधि-विधान से।',
    why_perform_en: '',
    why_perform_hi: '',
    how_to_participate_en: '',
    how_to_participate_hi: '',

    // Arrays
    puja_process: [''],

    // Packages
    packages: [
      {
        id: 'pkg_individual',
        name: 'Individual Devotee Sankalp',
        price: 951,
        tag: 'Most Chosen',
        devotees_count: 1,
        package_type: 'one_time',
        billing_period: 'one_time',
        benefits: ['Sankalp chanted with 1 Devotee Name & Gotra', 'Full Puja Video shared on WhatsApp', 'Blessed Prasad dispatched to your address'],
        description: 'Ideal for individuals seeking blessings for personal growth and peace.'
      },
      {
        id: 'pkg_partner',
        name: 'Partner / Couple Sankalp',
        price: 1551,
        tag: 'Popular',
        devotees_count: 2,
        package_type: 'one_time',
        billing_period: 'one_time',
        benefits: ['Sankalp chanted for 2 Devotees (Husband & Wife / Partners)', 'Full Puja Video proof', 'Consecrated Prasad kit delivered home'],
        description: 'Perfect for couples praying for marital bliss and harmony.'
      },
      {
        id: 'pkg_family',
        name: 'Complete Family Samuhik Sankalp',
        price: 2551,
        tag: 'Best Value',
        devotees_count: 4,
        package_type: 'one_time',
        billing_period: 'one_time',
        benefits: ['Sankalp for up to 4 Family Members with Gotra', 'Special Archana & Bhog Arpan', 'Express Prasad Dispatch'],
        description: 'Comprehensive family protection and collective ancestral blessings.'
      }
    ],

    // Point 5: Selected Bhets
    bhet_ids: [],

    // Point 6: Selected Store Product Add-ons
    addon_product_ids: [],

    // FAQs
    faqs: [
      {
        question: 'Do I need to be physically present at the temple?',
        answer: 'No, your physical presence is not required. The Vedic rituals and sankalp are performed by qualified priests using your name and gotra. Full video proof will be sent on WhatsApp.'
      },
      {
        question: 'How and when will I receive the sacred prasad?',
        answer: 'Blessed prasad is carefully packed and dispatched through our courier partners within 48 hours of puja completion. It typically arrives in 5-7 business days.'
      }
    ],

    // Testimonials
    testimonials: [
      {
        name: 'Pooja Sharma',
        location: 'Jaipur, Rajasthan',
        rating: 5,
        quote: 'Received full video with my gotra clearly recited. The prasad arrived beautifully packed. Har Har Mahadev!',
        verified: true
      }
    ]
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const [templesRes, deitiesRes, typesRes, bhetsRes, prodsRes] = await Promise.allSettled([
        axios.get(`${API}/temples`),
        axios.get(`${API}/deities`),
        axios.get(`${API}/temple-types`),
        axios.get(`${API}/bhets`),
        fetchPujaProducts()
      ]);

      if (templesRes.status === 'fulfilled' && Array.isArray(templesRes.value.data)) {
        setTemples(templesRes.value.data);
      }
      if (deitiesRes.status === 'fulfilled' && Array.isArray(deitiesRes.value.data)) {
        setDeities(deitiesRes.value.data);
      }
      if (typesRes.status === 'fulfilled' && Array.isArray(typesRes.value.data)) {
        setTempleTypes(typesRes.value.data);
      }
      if (bhetsRes.status === 'fulfilled' && Array.isArray(bhetsRes.value.data)) {
        setAllBhets(bhetsRes.value.data.filter(isAvailableOffering));
      }
      if (prodsRes.status === 'fulfilled') {
        setAllProducts(prodsRes.value);
      }
    } catch (err) {
      console.error('Error fetching supporting data:', err);
    }
  };

  const handleNameChange = (e) => {
    const val = e.target.value;
    if (!isCustomSlug) {
      setFormData(prev => ({
        ...prev,
        name: val,
        slug: slugify(val)
      }));
    } else {
      setFormData(prev => ({ ...prev, name: val }));
    }
  };

  // Video File Upload handler
  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 100 * 1024 * 1024) {
      toast.error('Video size must be under 100MB');
      return;
    }

    const uploadData = new FormData();
    uploadData.append('video', file);

    setUploadingVideo(true);
    try {
      const res = await axios.post(`${API}/upload/video`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      if (res.data?.url) {
        setFormData(prev => ({ ...prev, video_url: res.data.url }));
        toast.success('Video uploaded successfully!');
      }
    } catch (err) {
      console.error('Video upload error:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload video');
    } finally {
      setUploadingVideo(false);
    }
  };

  // Package helpers
  const handlePackageChange = (index, field, value) => {
    const updated = [...formData.packages];
    updated[index][field] = value;
    setFormData({ ...formData, packages: updated });
  };

  const addPackage = () => {
    const newPkg = {
      id: `pkg_${Date.now()}`,
      name: '',
      price: '',
      tag: '',
      devotees_count: 1,
      package_type: 'one_time',
      billing_period: 'one_time',
      benefits: [''],
      description: ''
    };
    setFormData({ ...formData, packages: [...formData.packages, newPkg] });
  };

  const removePackage = (index) => {
    if (formData.packages.length <= 1) {
      toast.error('At least one package is required');
      return;
    }
    setFormData({ ...formData, packages: formData.packages.filter((_, i) => i !== index) });
  };

  // Bhet toggle
  const toggleBhet = (bhetId) => {
    setFormData(prev => {
      const current = prev.bhet_ids || [];
      const updated = current.includes(bhetId)
        ? current.filter(id => id !== bhetId)
        : [...current, bhetId];
      return { ...prev, bhet_ids: updated };
    });
  };

  // Product Addon toggle
  const toggleProductAddon = (prodId) => {
    setFormData(prev => {
      const current = prev.addon_product_ids || [];
      const updated = current.includes(prodId)
        ? current.filter(id => id !== prodId)
        : [...current, prodId];
      return { ...prev, addon_product_ids: updated };
    });
  };

  // FAQ helpers
  const handleFaqChange = (index, key, val) => {
    const updated = [...formData.faqs];
    updated[index][key] = val;
    setFormData({ ...formData, faqs: updated });
  };

  const addFaq = () => {
    setFormData({ ...formData, faqs: [...formData.faqs, { question: '', answer: '' }] });
  };

  const removeFaq = (index) => {
    setFormData({ ...formData, faqs: formData.faqs.filter((_, i) => i !== index) });
  };

  // Testimonial helpers
  const handleTestimonialChange = (index, key, val) => {
    const updated = [...formData.testimonials];
    updated[index][key] = val;
    setFormData({ ...formData, testimonials: updated });
  };

  const addTestimonial = () => {
    setFormData({
      ...formData,
      testimonials: [...formData.testimonials, { name: '', location: '', rating: 5, quote: '', verified: true }]
    });
  };

  const removeTestimonial = (index) => {
    setFormData({ ...formData, testimonials: formData.testimonials.filter((_, i) => i !== index) });
  };

  // Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Puja name is required');
      return;
    }
    if (!formData.temple_id) {
      toast.error('Please select a temple');
      return;
    }
    if (!formData.image_url && (!formData.gallery || formData.gallery.length === 0)) {
      toast.error('Please upload at least one featured puja image');
      return;
    }

    setSubmitting(true);
    try {
      const formattedPackages = formatPujaPackages(formData.packages);

      const payload = {
        ...formData,
        price: getPujaStartingPrice(formattedPackages),
        image_url: formData.image_url || formData.gallery[0],
        gallery: formData.gallery.length > 0 ? formData.gallery : [formData.image_url],
        packages: formattedPackages,
        addons: [],
        puja_process: formData.puja_process.filter(p => p.trim()),
        faqs: formData.faqs.filter(f => f.question.trim()),
        testimonials: formData.testimonials.filter(t => t.name.trim() && t.quote.trim()),
        description: getPujaDescription(formData)
      };

      await axios.post(`${API}/pujas`, payload, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
      });

      toast.success('Sacred Puja ceremony created successfully!');
      navigate('/admin/pujas');
    } catch (error) {
      console.error('Error creating puja:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to create puja');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="puja-admin-form min-w-0 p-4 sm:p-8 max-w-6xl mx-auto space-y-8 pb-24">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white p-6 rounded-2xl border border-gray-200/80 shadow-xs">
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/admin/pujas">
            <Button variant="ghost" size="icon" className="h-10 w-10 text-gray-500 hover:text-gray-900 rounded-xl">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-2 min-w-0">
              <h1 className="font-heading text-2xl font-bold text-gray-900">Add New Sacred Puja</h1>
              <span className="bg-orange-50 text-[#FF7B00] border border-orange-200/60 text-xs px-2.5 py-0.5 rounded-full font-bold">
                Admin
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Create a puja with photos, bilingual descriptions, offerings and packages.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Link to="/admin/pujas">
            <Button variant="outline" className="rounded-xl">Cancel</Button>
          </Link>
          <Button
            type="submit"
            form="puja-admin-form"
            disabled={submitting}
            className="btn-saffron flex items-center gap-2 px-6 rounded-xl"
          >
            <Save className="w-4 h-4" />
            {submitting ? 'Creating Puja...' : 'Publish Sacred Puja'}
          </Button>
        </div>
      </div>

      <form id="puja-admin-form" onSubmit={handleSubmit} className="space-y-8">
        {/* ═══ 1. BASIC INFORMATION CARD ═══ */}
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs p-6 space-y-5">
          <h2 className="font-heading text-base font-bold text-gray-900 flex items-center gap-2 pb-3 border-b border-gray-100">
            <Flame className="w-5 h-5 text-[#FF7B00]" />
            Basic Puja Details
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label className="text-xs font-semibold text-gray-700">Puja Title / Name *</Label>
              <Input
                required
                placeholder="e.g. Mahamrityunjaya Shanti Mahayagya"
                value={formData.name}
                onChange={handleNameChange}
                className="mt-1.5"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold text-gray-700">URL Slug</Label>
                <button
                  type="button"
                  onClick={() => setIsCustomSlug(!isCustomSlug)}
                  className="text-[11px] text-[#FF7B00] hover:underline font-semibold"
                >
                  {isCustomSlug ? 'Auto-generate from Name' : 'Customize Slug'}
                </button>
              </div>
              <Input
                placeholder="e.g. mahamrityunjaya-shanti-mahayagya"
                value={formData.slug}
                disabled={!isCustomSlug}
                onChange={(e) => setFormData({ ...formData, slug: slugify(e.target.value) })}
                className="mt-1.5"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div>
              <Label className="text-xs font-semibold text-gray-700">Select Holy Temple *</Label>
              <select
                required
                value={formData.temple_id}
                onChange={(e) => setFormData({ ...formData, temple_id: e.target.value })}
                className="w-full mt-1.5 h-10 px-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FF7B00]/20 focus:border-[#FF7B00]"
              >
                <option value="">-- Choose Temple --</option>
                {temples.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.city || t.location || 'India'})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label className="text-xs font-semibold text-gray-700">Presiding Deity</Label>
              <Input
                placeholder="e.g. Mahadev / Shri Ganesh"
                list="deities-list"
                value={formData.deity}
                onChange={(e) => setFormData({ ...formData, deity: e.target.value })}
                className="mt-1.5"
              />
              <datalist id="deities-list">
                {deities.map(d => (
                  <option key={d.id || d.name} value={d.name} />
                ))}
              </datalist>
            </div>

            <div>
              <Label className="text-xs font-semibold text-gray-700">Temple Type / Circuit</Label>
              <Input
                placeholder="e.g. Jyotirlinga / Shaktipeeth"
                list="types-list"
                value={formData.temple_type}
                onChange={(e) => setFormData({ ...formData, temple_type: e.target.value })}
                className="mt-1.5"
              />
              <datalist id="types-list">
                {templeTypes.map(t => (
                  <option key={t.id || t.name} value={t.name} />
                ))}
              </datalist>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-xs font-semibold text-gray-700">Duration</Label>
              <Input
                placeholder="e.g. 1-2 hours"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="puja-date" className="text-xs font-semibold text-gray-700">Puja Date / Tithi</Label>
              <DatePicker id="puja-date" value={formData.puja_date}
                onChange={(puja_date) => setFormData(prev => ({ ...prev, puja_date }))} />
            </div>

          </div>

          {/* Flags */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-2 min-w-0">
              <input
                type="checkbox"
                id="is-active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded border-gray-300 text-[#FF7B00] focus:ring-[#FF7B00]"
              />
              <label htmlFor="is-active" className="text-sm font-medium text-gray-700 cursor-pointer">
                Puja is Active &amp; Visible
              </label>
            </div>

            <div className="flex items-center gap-2 min-w-0">
              <input
                type="checkbox"
                id="is-weekly"
                checked={formData.is_weekly}
                onChange={(e) => setFormData({ ...formData, is_weekly: e.target.checked })}
                className="rounded border-gray-300 text-[#FF7B00] focus:ring-[#FF7B00]"
              />
              <label htmlFor="is-weekly" className="text-sm font-medium text-gray-700 cursor-pointer">
                Weekly Recurring Puja
              </label>
            </div>

            {formData.is_weekly && (
              <div>
                <select
                  value={formData.weekly_day}
                  onChange={(e) => setFormData({ ...formData, weekly_day: e.target.value })}
                  className="w-full h-9 px-2 text-xs rounded-xl border border-gray-200 bg-white"
                >
                  {DAYS_OF_WEEK.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* ═══ 2. S3 BUCKET MEDIA & GALLERY (Point 2) ═══ */}
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs p-6 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3 border-b border-gray-100">
            <div>
              <h2 className="font-heading text-base font-bold text-gray-900 flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-[#FF7B00]" />
                Media &amp; S3 Image Gallery (Max 10 Images)
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Upload authentic temple &amp; deity photos directly to S3. First image acts as the primary thumbnail.
              </p>
            </div>
            <span className="text-xs text-gray-500 font-semibold bg-gray-100 px-3 py-1 rounded-full">
              {formData.gallery.length} / 10 images
            </span>
          </div>

          <ProductImageUploader
            featuredImage={formData.image_url}
            galleryImages={formData.gallery}
            onFeaturedChange={(url) => setFormData(prev => ({ ...prev, image_url: url }))}
            onGalleryChange={(urls) => setFormData(prev => ({ ...prev, gallery: urls }))}
          />

          {/* Video Section */}
          <div className="pt-4 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                <Video className="w-4 h-4 text-[#FF7B00]" />
                Direct S3 Video File Upload (MP4 / WebM)
              </Label>
              <div className="mt-1.5 space-y-2">
                <input
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime"
                  onChange={handleVideoUpload}
                  disabled={uploadingVideo}
                  className="w-full text-xs text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-orange-50 file:text-[#FF7B00] hover:file:bg-orange-100 cursor-pointer"
                />
                {uploadingVideo && (
                  <p className="text-xs text-[#FF7B00] font-medium animate-pulse">Uploading video to S3 storage...</p>
                )}
                {formData.video_url && (
                  <p className="text-xs text-emerald-600 font-medium truncate">
                    Uploaded: {formData.video_url}
                  </p>
                )}
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                <Youtube className="w-4 h-4 text-red-600" />
                Or YouTube Live / Darshan URL
              </Label>
              <Input
                placeholder="https://www.youtube.com/watch?v=..."
                value={formData.youtube_url}
                onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                className="mt-1.5"
              />
            </div>
          </div>
        </div>

        {/* ═══ 3. RICH CONTENT & DESCRIPTIONS (BILINGUAL) (Point 3) ═══ */}
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs p-6 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3 border-b border-gray-100">
            <div>
              <h2 className="font-heading text-base font-bold text-gray-900 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#FF7B00]" />
                Rich Content &amp; Descriptions (Bilingual)
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Provide comprehensive information for "About Puja, Mandir &amp; Bhagwan", "Puja Performed By", and "Why Perform".
              </p>
            </div>

            {/* Language Switcher */}
            <div className="inline-flex p-1 bg-gray-100 rounded-xl self-start sm:self-auto">
              <button
                type="button"
                onClick={() => setActiveTab('en')}
                className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all ${
                  activeTab === 'en' 
                    ? 'bg-white text-gray-900 shadow-xs' 
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                English
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('hi')}
                className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all ${
                  activeTab === 'hi' 
                    ? 'bg-[#FF7B00] text-white shadow-xs' 
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                हिंदी (Hindi)
              </button>
            </div>
          </div>

          <div className="space-y-6" key={activeTab}>
            <RichTextEditor
              label={activeTab === 'en' ? 'About - Puja, Mandir & Bhagwan (English)' : 'पूजा, मंदिर एवं भगवान परिचय (हिंदी)'}
              value={formData[`about_content_${activeTab}`]}
              onChange={value => setFormData(prev => ({ ...prev, [`about_content_${activeTab}`]: value }))}
              placeholder={activeTab === 'en' ? 'Describe the puja, temple and presiding deity...' : 'पूजा, मंदिर एवं आराध्य देवता का विवरण लिखें...'}
            />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <RichTextEditor
                label={activeTab === 'en' ? 'Puja Performed By (English)' : 'पूजा किसके द्वारा संपन्न होगी? (हिंदी)'}
                value={formData[`puja_performed_by_${activeTab}`]}
                onChange={value => setFormData(prev => ({ ...prev, [`puja_performed_by_${activeTab}`]: value }))}
              />
              <RichTextEditor
                label={activeTab === 'en' ? 'Why Perform This Puja? (English)' : 'यह पूजा क्यों कराएं? (हिंदी)'}
                value={formData[`why_perform_${activeTab}`]}
                onChange={value => setFormData(prev => ({ ...prev, [`why_perform_${activeTab}`]: value }))}
              />
            </div>
          </div>
        </div>

        {/* ═══ 4. PACKAGES & SUBSCRIPTION TIERS ═══ */}
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs p-6 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3 border-b border-gray-100">
            <div>
              <h2 className="font-heading text-base font-bold text-gray-900 flex items-center gap-2">
                <Tag className="w-5 h-5 text-[#FF7B00]" />
                Sankalp Packages &amp; Pricing Tiers
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Define Individual, Couple, and Family packages. You can also configure recurring subscription packages.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addPackage}
              className="text-xs border-dashed border-[#FF7B00] text-[#FF7B00] hover:bg-orange-50 rounded-xl"
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Add Package
            </Button>
          </div>

          <div className="space-y-4">
            {formData.packages.map((pkg, idx) => (
              <div key={idx} className="p-4 rounded-xl border border-gray-200 bg-[#FAF8F5] space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-[#FF7B00] text-white flex items-center justify-center text-xs">
                      {idx + 1}
                    </span>
                    Package #{idx + 1}
                  </span>
                  {formData.packages.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removePackage(idx)}
                      className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Remove
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                  <div className="sm:col-span-2">
                    <Label className="text-xs font-semibold text-gray-700">Package Name *</Label>
                    <Input
                      required
                      placeholder="e.g. Individual Devotee Sankalp"
                      value={pkg.name}
                      onChange={(e) => handlePackageChange(idx, 'name', e.target.value)}
                      className="mt-1 bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-gray-700">Price (₹) *</Label>
                    <Input
                      required
                      type="number"
                      min="0"
                      placeholder="951"
                      value={pkg.price}
                      onChange={(e) => handlePackageChange(idx, 'price', e.target.value)}
                      className="mt-1 bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-gray-700">Highlight Tag</Label>
                    <Input
                      placeholder="e.g. Most Chosen"
                      value={pkg.tag}
                      onChange={(e) => handlePackageChange(idx, 'tag', e.target.value)}
                      className="mt-1 bg-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs font-semibold text-gray-700">Devotees Count</Label>
                    <Input
                      type="number"
                      min="1"
                      value={pkg.devotees_count}
                      onChange={(e) => handlePackageChange(idx, 'devotees_count', e.target.value)}
                      className="mt-1 bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-gray-700">Package Type</Label>
                    <select
                      value={pkg.package_type || 'one_time'}
                      onChange={(e) => handlePackageChange(idx, 'package_type', e.target.value)}
                      className="w-full mt-1 h-10 px-3 rounded-xl border border-gray-200 bg-white text-sm"
                    >
                      <option value="one_time">One-Time Ritual</option>
                      <option value="subscription">Subscription (Monthly/Weekly)</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-gray-700">Short Description</Label>
                    <Input
                      placeholder="Brief note on who this package suits..."
                      value={pkg.description || ''}
                      onChange={(e) => handlePackageChange(idx, 'description', e.target.value)}
                      className="mt-1 bg-white"
                    />
                  </div>
                </div>
                <div className="space-y-3 border-t border-gray-200 pt-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Label className="text-xs font-bold text-gray-800">Key Benefits of Puja</Label>
                    <Button type="button" variant="ghost" size="sm"
                      onClick={() => handlePackageChange(idx, 'benefits', [...(pkg.benefits || []), ''])}>
                      <Plus className="mr-1 h-4 w-4" /> Add Benefit
                    </Button>
                  </div>
                  {(pkg.benefits || []).map((benefit, benefitIndex) => (
                    <div key={benefitIndex} className="flex items-center gap-2 min-w-0">
                      <Input value={benefit} placeholder={`Benefit ${benefitIndex + 1}`}
                        aria-label={`Package ${idx + 1} benefit ${benefitIndex + 1}`}
                        onChange={e => handlePackageChange(idx, 'benefits', pkg.benefits.map((item, i) => i === benefitIndex ? e.target.value : item))} />
                      <Button type="button" variant="ghost" size="icon" className="shrink-0 text-gray-400 hover:text-red-600"
                        aria-label={`Remove benefit ${benefitIndex + 1} from package ${idx + 1}`}
                        onClick={() => handlePackageChange(idx, 'benefits', pkg.benefits.filter((_, i) => i !== benefitIndex))}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ═══ 5. BHET OFFERINGS SELECTION ═══ */}
        {allBhets.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs p-6 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3 border-b border-gray-100">
            <div>
              <h2 className="font-heading text-base font-bold text-gray-900 flex items-center gap-2">
                <Gift className="w-5 h-5 text-[#FF7B00]" />
                Select Bhet &amp; Daan Offerings
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Choose which sacred Bhet items (Vastra Daan, Gau Seva, Brahman Bhojan) devotees can add to this Puja.
              </p>
            </div>
            <Link to="/admin/bhets" target="_blank" className="text-xs text-[#FF7B00] hover:underline font-bold">
              Manage Bhets Master &rarr;
            </Link>
          </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {allBhets.map((bhet) => {
                const isSelected = (formData.bhet_ids || []).includes(bhet.id);
                return (
                  <label
                    key={bhet.id}
                    className={`p-3.5 rounded-xl border cursor-pointer transition-all flex items-center gap-3 ${
                      isSelected
                        ? 'border-[#FF7B00] bg-orange-50/50 shadow-xs'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <input type="checkbox" checked={isSelected}
                      onChange={() => toggleBhet(bhet.id)} aria-label={bhet.name} />

                    <div className="w-10 h-10 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                      {bhet.image_url ? (
                        <img src={bhet.image_url} alt={bhet.name} className="w-full h-full object-cover" />
                      ) : (
                        <Gift className="w-5 h-5 text-gray-400 m-2.5" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-gray-900 truncate">{bhet.name}</p>
                      <p className="text-[11px] text-[#FF7B00] font-semibold">₹{bhet.price}</p>
                    </div>
                  </label>
                );
              })}
            </div>
        </div>
        )}

        {/* ═══ 6. STORE PRODUCT ADD-ONS SELECTION ═══ */}
        {allProducts.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs p-6 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3 border-b border-gray-100">
            <div>
              <h2 className="font-heading text-base font-bold text-gray-900 flex items-center gap-2">
                <Package className="w-5 h-5 text-[#FF7B00]" />
                Select Store Products as Add-ons
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Choose divine store items (e.g. Rudraksha, Yantra, Mala) to show as purchase add-ons during Puja booking.
              </p>
            </div>
            <Link to="/admin/products" target="_blank" className="text-xs text-[#FF7B00] hover:underline font-bold">
              Manage Products &rarr;
            </Link>
          </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-80 overflow-y-auto pr-1">
              {allProducts.map((prod) => {
                const isSelected = (formData.addon_product_ids || []).includes(prod.id);
                return (
                  <label
                    key={prod.id}
                    className={`p-3 rounded-xl border cursor-pointer transition-all flex items-center gap-3 ${
                      isSelected
                        ? 'border-[#FF7B00] bg-orange-50/50 shadow-xs'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <input type="checkbox" checked={isSelected}
                      onChange={() => toggleProductAddon(prod.id)} aria-label={prod.name} />

                    <div className="w-10 h-10 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                      {prod.image_url ? (
                        <img src={prod.image_url} alt={prod.name} className="w-full h-full object-cover" />
                      ) : (
                        <Package className="w-5 h-5 text-gray-400 m-2.5" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-gray-900 truncate">{prod.name}</p>
                      <p className="text-[11px] text-[#FF7B00] font-semibold">₹{prod.price}</p>
                    </div>
                  </label>
                );
              })}
            </div>
        </div>
        )}

        {/* ═══ 8. FAQS & TESTIMONIALS ═══ */}
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs p-6 space-y-6">
          <h2 className="font-heading text-base font-bold text-gray-900 pb-3 border-b border-gray-100 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-[#FF7B00]" />
            Frequently Asked Questions &amp; Devotee Reviews
          </h2>

          {/* FAQs */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-bold text-gray-800 uppercase tracking-wide">
                FAQs (Question &amp; Answer)
              </Label>
              <button
                type="button"
                onClick={addFaq}
                className="text-xs text-[#FF7B00] hover:underline font-bold flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" />
                Add FAQ
              </button>
            </div>

            {formData.faqs.map((f, i) => (
              <div key={i} className="p-3.5 rounded-xl border border-gray-200 bg-[#FAF8F5] space-y-2">
                <div className="flex items-center justify-between">
                  <Input
                    placeholder="Question (e.g. Do I need to be physically present?)"
                    value={f.question}
                    onChange={(e) => handleFaqChange(i, 'question', e.target.value)}
                    className="bg-white font-semibold"
                  />
                  <button
                    type="button"
                    onClick={() => removeFaq(i)}
                    className="ml-2 p-1.5 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <textarea
                  rows="2"
                  placeholder="Answer..."
                  value={f.answer}
                  onChange={(e) => handleFaqChange(i, 'answer', e.target.value)}
                  className="w-full p-2.5 text-xs rounded-xl border border-gray-200 bg-white"
                />
              </div>
            ))}
          </div>

          {/* Testimonials */}
          <div className="pt-4 border-t border-gray-100 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-bold text-gray-800 uppercase tracking-wide">
                Devotee Testimonials &amp; Experiences
              </Label>
              <button
                type="button"
                onClick={addTestimonial}
                className="text-xs text-[#FF7B00] hover:underline font-bold flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Review
              </button>
            </div>

            {formData.testimonials.map((t, i) => (
              <div key={i} className="p-3.5 rounded-xl border border-gray-200 bg-[#FAF8F5] space-y-2">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Input
                    placeholder="Devotee Name"
                    value={t.name}
                    onChange={(e) => handleTestimonialChange(i, 'name', e.target.value)}
                    className="bg-white"
                  />
                  <Input
                    placeholder="City, State"
                    value={t.location}
                    onChange={(e) => handleTestimonialChange(i, 'location', e.target.value)}
                    className="bg-white"
                  />
                  <div className="flex items-center gap-2 min-w-0">
                    <Input
                      type="number"
                      min="1"
                      max="5"
                      placeholder="Rating (1-5)"
                      value={t.rating}
                      onChange={(e) => handleTestimonialChange(i, 'rating', parseInt(e.target.value, 10) || 5)}
                      className="bg-white"
                    />
                    <button
                      type="button"
                      onClick={() => removeTestimonial(i)}
                      className="p-1.5 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <textarea
                  rows="2"
                  placeholder="Devotee experience / quote..."
                  value={t.quote}
                  onChange={(e) => handleTestimonialChange(i, 'quote', e.target.value)}
                  className="w-full p-2.5 text-xs rounded-xl border border-gray-200 bg-white"
                />
              </div>
            ))}
          </div>
        </div>

        {/* ═══ BOTTOM SUBMIT ACTIONS ═══ */}
        <div className="flex flex-wrap items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <Link to="/admin/pujas">
            <Button variant="outline" className="rounded-xl px-5">Cancel</Button>
          </Link>
          <Button
            type="submit"
            disabled={submitting}
            className="btn-saffron flex items-center gap-2 px-8 py-2.5 rounded-xl shadow-md"
          >
            <Save className="w-4 h-4" />
            {submitting ? 'Creating Puja...' : 'Publish Sacred Puja'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminPujaAdd;
